import React from 'react';
import { Box, List, ListItem, ListItemText, Skeleton } from '@mui/material';
import { row, rowText, rowTitle, meta } from '../../../styles/visualLanguage';

/** 2 content-shaped skeleton rows, never a spinner. */
const SKELETON_ROW_COUNT = 2;

/**
 * EngagementListSkeleton
 *
 * Content-shaped skeleton rows, rendered while the engagements channel has
 * not yet delivered a first snapshot. Skeletons (deliberately not a
 * spinner) are what keep the loading state visually distinct from the
 * empty state, which matters because both are reachable on first paint.
 *
 * Rows are plain, non-interactive `ListItem`s — never a clickable list
 * item — because a skeleton row has nothing to click yet. The row box,
 * hairline and type metrics now come from the shared `row`/`rowText`/
 * `rowTitle`/`meta` fragments — the same ones a real `EngagementRow`
 * consumes — so this skeleton cannot drift from a real row's geometry.
 * `rowTitle`'s parent-hover rule is inert here (a `Skeleton` renders no
 * text colour); that's accepted in exchange for guaranteed metric parity.
 *
 * The `role="status"`/`aria-live="polite"` live region lives on a WRAPPING
 * `Box`, never on the `List` itself — overriding the `<ul>`'s implicit role
 * would change how its `ListItem` children compute their own implicit
 * `listitem` role, and this component's own test
 * (`AdvisoryCalloutState.test.tsx`) asserts that count. This component
 * never receives focus: it is the first paint of `/advisory`, before
 * anything on the page has held focus, so there is nothing to hand off
 * from.
 */
export function EngagementListSkeleton() {
  return (
    <Box data-testid="advisory-list-skeleton" role="status" aria-live="polite">
      <List sx={{ p: 0 }}>
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
          <ListItem key={index} sx={[row]}>
            <ListItemText
              primary={<Skeleton variant="text" width="60%" sx={[rowTitle]} />}
              secondary={<Skeleton variant="text" width="40%" sx={[meta]} />}
              sx={[rowText]}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

export default EngagementListSkeleton;
